import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import {
  ApiService,
  MerchantPosTransaction,
  Order,
  OrderItem,
  Ticket,
} from '@hypenation/tickets';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { ShareTicketModalComponent } from '../../components/share-ticket-modal/share-ticket-modal.component';
import { Microservice } from '@hypenation/shared';
import { HttpResponse } from '@angular/common/http';
import { catchError, forkJoin, map, of } from 'rxjs';
import { environment } from '@env/environment';

@Component({
  selector: 'hypenation-order-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzCardModule,
    NzDescriptionsModule,
    NzTableModule,
    NzTagModule,
    NzButtonModule,
    NzIconModule,
    NzSpinModule,
    NzDividerModule,
    NzModalModule,
    ShareTicketModalComponent,
  ],
  templateUrl: './order-detail.component.html',
  styleUrl: './order-detail.component.css',
})
export class OrderDetailComponent implements OnInit {
  loading = false;
  orderNumber: string | null = null;
  order: Order | null = null;
  orderItems: OrderItem[] = [];
  tickets: Ticket[] = [];
  walletTransactionsByTicket: Record<string, MerchantPosTransaction[]> = {};
  walletTransactionsLoading = false;

  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(ApiService);
  private readonly modalService = inject(NzModalService);

  ngOnInit(): void {
    this.orderNumber = this.route.snapshot.paramMap.get('orderNumber');
    if (this.orderNumber) {
      this.loadOrderDetail();
    }
  }

  loadOrderDetail(): void {
    if (!this.orderNumber) return;
    this.loading = true;

    forkJoin({
      order: this.apiService.getOrderByOrderNumber(this.orderNumber),
      items: this.apiService.getItemsByOrderNumber(this.orderNumber),
      tickets: this.apiService.getTicketByOrderNumber(this.orderNumber),
    }).subscribe({
      next: (res) => {
        this.order = res.order.body;
        this.orderItems = res.items.body ?? [];
        this.tickets = res.tickets.body ?? [];
        this.loading = false;
        this.loadWalletTransactions();
      },
      error: () => (this.loading = false),
    });
  }

  loadWalletTransactions(): void {
    if (!this.tickets.length) {
      this.walletTransactionsByTicket = {};
      return;
    }

    this.walletTransactionsLoading = true;
    const requests = this.tickets.map((ticket) =>
      this.apiService.getWalletTransactionsByTicketUid(ticket.uid, 0, 5).pipe(
        map((res) => ({
          ticketUid: ticket.uid,
          transactions: res.body?.content ?? [],
        })),
        catchError(() =>
          of({
            ticketUid: ticket.uid,
            transactions: [] as MerchantPosTransaction[],
          })
        )
      )
    );

    forkJoin(requests).subscribe({
      next: (results) => {
        this.walletTransactionsByTicket = results.reduce<
          Record<string, MerchantPosTransaction[]>
        >((acc, result) => {
          acc[result.ticketUid] = result.transactions;
          return acc;
        }, {});
        this.walletTransactionsLoading = false;
      },
      error: () => (this.walletTransactionsLoading = false),
    });
  }

  formatCurrency(value: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(value);
  }

  /**
   * Accepts a number as well as a string because the audit timestamps this renders arrive as epoch
   * milliseconds. `new Date()` handles both a number and an ISO string, but returns "Invalid Date"
   * for a numeric string — so the widened type is the honest one, not a looser one.
   */
  formatDateTime(value: string | number | undefined | null): string {
    if (!value) return '-';
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  }

  getReceiptStatusColor(status: string | undefined): string {
    switch (status) {
      case 'SENT':
        return 'success';
      case 'PENDING':
        return 'processing';
      case 'FAILED':
        return 'error';
      case 'SKIPPED_NO_EMAIL':
        return 'default';
      default:
        return 'default';
    }
  }

  getStatusColor(status: string | undefined): string {
    if (!status) return 'default';
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      case 'IN_PROGRESS':
        return 'processing';
      default:
        return 'default';
    }
  }

  getTicketDownloadUrl(ticketUid: string): string {
    return `${environment.apiUrl}/${Microservice.ETICKETING}/opn/v2/download-tickets?uid=${ticketUid}`;
  }

  openShareModal(ticketUid: string): void {
    this.modalService.create({
      nzTitle: 'Share Ticket',
      nzContent: ShareTicketModalComponent,
      nzData: { ticketUid },
      nzFooter: null,
      nzWidth: 450
    });
  }
}
